import type { EventTheme } from "@/lib/event-schema";

// Translate the admin-chosen / banner-derived theme into the CSS custom
// properties that drive Lantern tokens. The accent family is overridden;
// the canvas/ink stays stable so dark mode keeps working.
//
// Two ink tokens cover the two text-on-color contexts:
//   --accent-ink: text on the pale accent-soft surface (always dark-ish).
//   --accent-on:  text on the solid accent color, picked by luminance so
//                 a dark green or navy primary gets light text and a pale
//                 yellow primary still gets dark text.

const INK_LIGHT: [number, number, number] = [250, 246, 232]; // warm near-white
const INK_DARK: [number, number, number] = [40, 26, 8];      // warm near-black

export function themeToCssVars(theme?: EventTheme): Record<string, string> | undefined {
  if (!theme?.primary) return undefined;
  const rgb = hexToRgb(theme.primary);
  if (!rgb) return undefined;
  const accentRgb = theme.accent ? hexToRgb(theme.accent) ?? rgb : rgb;
  const mode = theme.mode ?? "joyful";

  const accent = `${rgb[0]} ${rgb[1]} ${rgb[2]}`;
  const accentBright = `${accentRgb[0]} ${accentRgb[1]} ${accentRgb[2]}`;
  const soft = mode === "mourning" ? mix(rgb, 0.18, [10, 10, 14]) : mix(rgb, 0.16, [255, 245, 220]);
  const ink = mode === "mourning" ? mix(rgb, 0.7, [240, 230, 215]) : mix(rgb, 0.5, [50, 30, 8]);
  const accentOn = pickInkOn(rgb);

  return {
    "--accent": accent,
    "--accent-soft": `${soft[0]} ${soft[1]} ${soft[2]}`,
    "--accent-ink": `${ink[0]} ${ink[1]} ${ink[2]}`,
    "--accent-on": `${accentOn[0]} ${accentOn[1]} ${accentOn[2]}`,
    "--accent-bright": accentBright,
  };
}

function pickInkOn(accent: [number, number, number]): [number, number, number] {
  // WCAG contrast against both candidates; pick the higher-contrast one,
  // then warm-tint very slightly so it doesn't read as a flat grey.
  const lumAccent = relativeLuminance(accent);
  const lumLight = relativeLuminance(INK_LIGHT);
  const lumDark = relativeLuminance(INK_DARK);
  const useLight = contrast(lumAccent, lumLight) >= contrast(lumAccent, lumDark);
  return mix(useLight ? INK_LIGHT : INK_DARK, 0.05, accent);
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const toLin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

function contrast(a: number, b: number): number {
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  return (hi + 0.05) / (lo + 0.05);
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return null;
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

function mix(
  base: [number, number, number],
  ratio: number,
  toward: [number, number, number],
): [number, number, number] {
  return [
    Math.round(base[0] * (1 - ratio) + toward[0] * ratio),
    Math.round(base[1] * (1 - ratio) + toward[1] * ratio),
    Math.round(base[2] * (1 - ratio) + toward[2] * ratio),
  ];
}
